import React, { useEffect, useContext } from "react";
import { ProtectedWithLoginPopup } from "components/ProtectedWithLoginPopup";
import { clientRoutes } from "helpers/clientRoutes";
import { AuthContext } from "components/AuthProvider";
import {
  getPatientDetails,
  getPatientAttributes,
  getUrlParam,
  HomePageEvents,
} from "helpers/commonHelpers";
import { useAuth } from "hooks/authHooks";
import Link from "next/link";
import { eventTracking } from "clevertapTracking";
import styles from "./OurServices.module.scss";
import { dataLayerTracking } from "gtmTracking";
import { SignedinState } from "../../components/enums/SignedinState";
import useMobileDetect from "helpers/useMobileDetect";
import { healthRecordsCountFn } from "helpers/commonHelpers";

type ServiceItem = {
  title: string;
  content: string;
  imgUrl: string;
  action: {
    link: string;
    content: string;
  };
};

interface ServiceItemProps {
  item: ServiceItem;
}

const ServiceItem: React.FC<ServiceItemProps> = (props) => {
  const { title, imgUrl, content, action } = props.item;
  const { getPhrNotifyEvent } = useContext(AuthContext);
  const currentDevice = useMobileDetect();
  const headTagCondition =
    title === "Book Appointment with Apollo Doctors" ||
    title === "Buy Medicines and Essentials";
  const { isSignedIn, currentPatient } = useAuth();
  const patientDetails = getPatientDetails(currentPatient);
  const [reqParamsForTeams, setReqParamsForTeams] = React.useState<
    string | null
  >(null);

  useEffect(() => {
    if (isSignedIn) {
      const source = getUrlParam("utm_source");
      const reqParamsForTeams = source
        ? `?utm_source=${source}&t=${encodeURIComponent(getUrlParam("t"))}`
        : null;
      setReqParamsForTeams(reqParamsForTeams);
    }
  }, [isSignedIn]);

  return (
    <ProtectedWithLoginPopup>
      {({ protectWithLoginPopup }) => (
        <li className={styles.serviceItem}>
          <div
            className={
              title === "Book Appointment with Apollo Doctors" ||
              title === "Buy Medicines and Essentials"
                ? `${styles.serviceItemIn} ${styles.serviceItemBtn}`
                : `${styles.serviceItemIn}`
            }
          >
            <Link
              href={`${action.link}${
                reqParamsForTeams ? `${reqParamsForTeams}` : ""
              }`}
              prefetch={false}
            >
              <a
                onClick={(e) => {
                  localStorage.setItem("HomePageClicked", "homepage");
                  if (action.link === "") {
                    protectWithLoginPopup();
                  }
                  if (title === "Buy Medicines and Essentials") {
                    dataLayerTracking({
                      event: HomePageEvents.event,
                      clicked_section: HomePageEvents.lob_button,
                      clicked_item_name: HomePageEvents.buy_medicines,
                    });
                    eventTracking(
                      "Pharmacy Home page viewed",
                      isSignedIn
                        ? {
                            ...getPatientAttributes(patientDetails),
                            "Nav src": "Hero Banner",
                            "Page Name": "Home Screen",
                          }
                        : {
                            "Nav src": "Hero Banner",
                            "Page Name": "Home Screen",
                          }
                    );

                    isSignedIn &&
                      eventTracking("Pharmacy Buy Medicine clicked", {
                        ...getPatientAttributes(patientDetails),
                        "Nav src": "Hero Banner",
                        "Page Name": "Home Screen",
                      });
                  }

                  if (
                    isSignedIn &&
                    title === "Book Appointment with Apollo Doctors"
                  ) {
                    dataLayerTracking({
                      event: HomePageEvents.event,
                      clicked_section: HomePageEvents.lob_button,
                      clicked_item_name: HomePageEvents.book_appointment,
                    });
                    eventTracking(
                      "Consult Homescreen Book doctor appointment clicked",
                      {
                        ...getPatientAttributes(patientDetails),
                        "Nav src": "Hero Banner",
                        Relation: currentPatient?.relation,
                        isConsulted: patientDetails?.isConsulted
                          ? "Repeat"
                          : "New",
                      }
                    );
                  }
                  if (isSignedIn && title === "View Health Records") {
                    dataLayerTracking({
                      event: HomePageEvents.event,
                      clicked_section: HomePageEvents.lob_button,
                      clicked_item_name: HomePageEvents.view_health_record,
                    });
                    eventTracking(`PHR Click Health Record`, {
                      ...getPatientAttributes(patientDetails),
                      "Nav src": "Hero Banner",
                      isMWeb: currentDevice?.isMobile(),
                      "Guest User": !patientDetails?.patientId,
                    });
                  }
                  if (title === "Book Lab Tests") {
                    /* WEB ENGAGE TRACKING */
                    const circle_subs = localStorage.getItem(
                      "userSubscriptions"
                    )
                      ? JSON.parse(localStorage.getItem("userSubscriptions"))
                      : {};
                    const address =
                      localStorage.getItem("diagnosticAddress") &&
                      JSON.parse(localStorage.getItem("diagnosticAddress"));
                    const clevertapData = {
                      ...getPatientAttributes(patientDetails),
                      Circle_user:
                        circle_subs?.APOLLO &&
                        circle_subs?.APOLLO[0]?.status === "active"
                          ? true
                          : false,
                      Source: "247 Home CTA",
                      City: address ? address?.cityName : "Not available",
                      CityId: address ? address?.cityID : "Not available",
                    };
                    dataLayerTracking({
                      event: HomePageEvents.event,
                      clicked_section: HomePageEvents.lob_button,
                      clicked_item_name: HomePageEvents.book_lab_test,
                    });
                    eventTracking(
                      "Diagnostic landing page viewed",
                      clevertapData
                    );
                    const diag_event_data = {
                      ...getPatientAttributes(patientDetails),
                      CTA: "Main Home CTA",
                      Relation: currentPatient?.relation || "",
                      "User Type":
                        (currentPatient && currentPatient.isConsulted
                          ? "Repeat"
                          : "New") || "",
                      City: address ? address?.cityName : "Not available",
                      CityId: address ? address?.cityID : "Not available",
                    };
                    eventTracking(
                      "Home page diagnostics CTA clicked",
                      diag_event_data
                    );
                  }
                }}
                title={title}
              >
                <div className={styles.serviceIcon}>
                  <img
                    src={imgUrl}
                    width="40"
                    height="40"
                    alt="Apollo 24|7 Services"
                  />
                </div>
                {title === "View Health Records" ? (
                  <div className={styles.serviceInfo}>
                    <h3 title={title}>{title}</h3>
                    {healthRecordsCountFn(getPhrNotifyEvent) > 0 && (
                      <span className={styles.healthrecordsCount}>
                        {healthRecordsCountFn(getPhrNotifyEvent)}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className={styles.serviceInfo}>
                    <h3 title={title}>{title}</h3>
                  </div>
                )}

                <img
                  className={
                    title === "Book Appointment with Apollo Doctors" ||
                    title === "Buy Medicines and Essentials"
                      ? styles.rightArrow
                      : styles.displayNone
                  }
                  src={`${process.env.NEXT_PUBLIC_247_CDN_URL}/images/ic_arrow_right_white.svg`}
                  alt=""
                />
              </a>
            </Link>
          </div>
        </li>
      )}
    </ProtectedWithLoginPopup>
  );
};

const OurServices: React.FC = (props) => {
  const { isSignedIn } = useAuth();
  const serviceItems: ServiceItem[] = [
    {
      title: "Book Appointment with Apollo Doctors",
      content: `Let's get you connected with them.`,
      imgUrl: `${process.env.NEXT_PUBLIC_247_CDN_URL}/images/ic-doctor.svg`,
      action: {
        link: `${clientRoutes.specialityListing()}?source=home_page`,
        content: "Find specialist",
      },
    },
    {
      title: `Buy Medicines and Essentials`,
      content: "You can search by name or prescription.",
      imgUrl: `${process.env.NEXT_PUBLIC_247_CDN_URL}/images/ic_medicines.png`,
      action: { link: clientRoutes.medicines(), content: "Search Medicine" },
    },
    {
      title: "Book Lab Tests",
      content: "Learn about our Star Doctors Program.",
      imgUrl: `${process.env.NEXT_PUBLIC_247_CDN_URL}/images/ic_home_test.svg`,
      action: {
        link: clientRoutes.tests(),
        content: "Who are star doctors",
      },
    },
    {
      title: "View Health Records",
      content: "Learn about our Start Doctors Program.",
      imgUrl: `${process.env.NEXT_PUBLIC_247_CDN_URL}/images/ic-prescription.svg`,
      action: {
        link:
          isSignedIn === SignedinState.SIGNEDIN
            ? clientRoutes.healthRecords("")
            : "",
        content: "Who are star doctors",
      },
    },
  ];

  return (
    <ul className={styles.serviceList}>
      {serviceItems.map((item, i) => (
        <ServiceItem item={item} key={i} />
      ))}
    </ul>
  );
};

export default OurServices;
